import { useCallback, useMemo, useState } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSpinner,
  Icon,
} from '@hap/components'

import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

export function DnisCommandList({
  placeholder = 'DNIS',
  selected = [],
  onSelect,
  multiselect,
}: BaseCommandListProps<false, string>) {
  const [query, setQuery] = useState('')
  const { data, isInitialLoading } = useDatastore(FilterCollection.Dnis)

  const isSearching = query.trim().length > 0

  const visibleItems = useMemo(() => {
    if (!isSearching) return data
    const normalizedQuery = query.toLowerCase()
    return data?.filter((dnis) => dnis.name.toLowerCase().includes(normalizedQuery))
  }, [data, query, isSearching])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      onSelect(newSelection)
    },
    [onSelect],
  )

  const handleToggleItem = useCallback(
    (id: string) => {
      if (multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        onSelect([id])
      }
    },
    [multiselect, selected, onSelect],
  )

  const handleClear = useCallback(() => {
    onSelect([])
  }, [onSelect])

  return isInitialLoading ? (
    <CommandSpinner />
  ) : (
    <Command>
      {(data?.length || 0) > 10 && (
        <CommandInput
          placeholder={placeholder}
          autoFocus
          value={query}
          onValueChange={setQuery}
        />
      )}
      <CommandList className={(data?.length || 0) > 10 ? 'py-1' : ''}>
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={multiselect}
          />
          {selected.length > 0 && (
            <CommandItem onSelect={handleClear} className="gap-2">
              <Icon name="x" />
              Clear
            </CommandItem>
          )}
          {visibleItems?.map((dnis) => (
            <CommandCheckItem
              key={dnis.id}
              value={dnis.id}
              keywords={[dnis.name]}
              className="group mx-1 grid gap-3"
              selected={selected.includes(dnis.id)}
              onSelect={() => handleToggleItem(dnis.id)}
              multiselect={multiselect}
            >
              {dnis.name}
            </CommandCheckItem>
          ))}
        </CommandGroup>
        <CommandEmpty>No DNIS found</CommandEmpty>
      </CommandList>
    </Command>
  )
}
